<script setup>
import { useHead, useHeadRaw } from '@vueuse/head'
import { useTranslation } from 'i18next-vue'
import { computed, onServerPrefetch } from 'vue'
import { useStore } from 'vuex'

import useWork from '@mixins/Work'
import {
  LayoutContentContainer as ContentContainer,
  SearchResultList as ResultList
} from '@web-nfb/vue-3-components'
import NotFound from '@pages/NotFound'
import Loading from '@components/Loading'
import UnderlinedTitle from '@components/UnderlinedTitle'
import Info from '@components/Detail/Info'
import Subjects from '@components/Detail/Subjects'
import FullCredits from '@components/Detail/FullCredits'
import Availability from '@components/Detail/Availability'
import OppositeWorkList from '@components/Detail/OppositeWorkList'
import Awards from '@components/Detail/Awards'
import ResultCard from '@components/Search/ResultCard'

const { t } = useTranslation()
const store = useStore()

const workType = 'film'
const jsonType = 'Movie'

const {
  metaTitle,
  metaLinks,
  jsonLdToString,
  item,
  loading,
  infoProps,
  loadByTypeAndSlugOrId
} = useWork(workType, jsonType)

const shouldUseServerPrefetch = computed(() => store.getters['Global/shouldUseServerPrefetch'])

useHead({
  title: metaTitle,
  link: metaLinks
})

useHeadRaw({
  script: [
    {
      type: 'application/ld+json',
      innerHTML: jsonLdToString
    }
  ]
})

onServerPrefetch(async () => {
  if (shouldUseServerPrefetch.value) {
    await loadByTypeAndSlugOrId()
    await item.value
  }
})

const hasAvailability = computed(() =>
  (item.value.availability?.resource_url && item.value.availability.is_public) ||
  item.value.availability?.buy ||
  (item.value.availability?.dvd && !!item.value.dvd_link)
)

</script>
<template>
  <div
    class="film-item"
    v-if="item || loading"
  >
    <ContentContainer v-if="loading">
      <Loading />
    </ContentContainer>
    <template v-else>
      <ContentContainer>
        <UnderlinedTitle
          class="align-with-logo"
          :title="t('home.title')"
          tag-title="p"
          tag-class="h4"
        />
        <Info
          v-bind="infoProps"
          :type="workType"
        />
      </ContentContainer>

      <ContentContainer
        v-if="item.parent_series"
        class="parent-serie-container"
        data-cy="parent-series"
      >
        <h3
          class="h3 uppercase"
          v-html="t('films.partOfSeries')"
        />
        <div class="row">
          <div class="col-sm-12 col-md-8">
            <ResultList layout="compact">
              <ResultCard
                v-for="serie in item.parent_series"
                :key="`${serie.id}-${serie.slug}`"
                :item="serie"
                :thumb-pattern="infoProps.mediaPattern"
              />
            </ResultList>
          </div>
        </div>
      </ContentContainer>

      <ContentContainer>
        <Availability
          v-if="hasAvailability"
          :resource-u-r-l="item.availability.resource_url"
          :has-d-v-d="!!item.availability.dvd"
          :buy="item.availability.buy"
          :is-public="item.availability.is_public"
          :rent="item.availability.rent"
        />
        <OppositeWorkList
          v-if="item.opposite_work_list && item.opposite_work_list.length > 0"
          :opposite-work-list="item.opposite_work_list"
        />
        <Subjects
          v-if="item.subjects && item.subjects.length"
          :subjects="item.subjects"
        />
        <FullCredits
          v-if="item.credits && item.credits.length"
          :credits="item.credits"
        />
        <Awards
          v-if="item.awards && item.awards.length"
          :awards="item.awards"
        />
      </ContentContainer>
    </template>
  </div>
  <NotFound
    v-else-if="!loading"
    code="404"
  />
</template>

<style scoped lang="less">
@import (reference) "~@less/shared/utilities.less";
.film-item {
  background: @white;
}

.parent-serie-container {
  background: @grey-20;
  margin: 1rem 0;

  .h3 {
    margin-top: 2rem;
    margin-bottom: 0;
  }
}
</style>
